import React, { useCallback, useEffect, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { StatusBar, Tooltip } from "@nokia-csf-uxr/ccfk";
import {
  StatusBarPageGroup,
  StatusBarPagination,
  StatusBarPaginationIcon,
  StatusBarPaginationInput,
  StatusBarSection,
  StatusBarText,
  StatusBarTextTitle,
} from "@nokia-csf-uxr/ccfk/StatusBar";

import ChevronLeftIcon from "@nokia-csf-uxr/ccfk-assets/latest/ChevronLeftIcon";
import ChevronRightIcon from "@nokia-csf-uxr/ccfk-assets/latest/ChevronRightIcon";

import FirstPageIcon from "@nokia-csf-uxr/ccfk-assets/latest/ChevronFirstIcon";
import LastPageIcon from "@nokia-csf-uxr/ccfk-assets/latest/ChevronLastIcon";
import { PAGINATION_ITEMS_PER_PAGE } from "features/dashboard/utils/consstants";

import { RootState } from "Store/mainStore";

import { setProperty } from "features/dashboard/store/dashboardSlice";
// import PaginationSkeleton from "./PaginationSkelton";

const KEY_CODES = {
  ARROW_UP: "ArrowUp",
  ARROW_DOWN: "ArrowDown",
  ARROW_LEFT: "ArrowLeft",
  ARROW_RIGHT: "ArrowRight",
};
const START_PAGE = 1;


const CasePagination = () => {
  const [currentPageCount, setCurrentPageCount] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  // Sectors
  const dashboardSelector = useSelector((state: RootState) => state.dashboard);
  const [currentPage, setCurrentPage] = useState(dashboardSelector.data?.pagination?.["currentPage"]);

  // Hooks
  const dispatch = useDispatch();

  // Set the initial current page to the start page if it's not set
  const validatePage = useCallback((page) => {
    const { pageCount } = prepareTotalPages();
    if (page <= START_PAGE) {
      return START_PAGE;
    }
    if (page > pageCount) {
      return pageCount;
    }

    return page;
  }, [START_PAGE, totalPages]);


  /**
   * Updates the pagination settings by setting the number of items displayed per page
   * and resetting the current page to the start page.
   *
   * @param value - The new limit for the number of items per page.
   */
  const handleItemsPerPgeLimit = useCallback((value) => {
    // Get current pagination object from dashboardSelector
    const __pagination = _.cloneDeep(dashboardSelector.data?.pagination);
    // Update the limit per page
    __pagination["itemsPerPage"] = parseInt(value);
    // Update the current page to 1
    __pagination["currentPage"] = START_PAGE;
    // Dispatch the updated pagination object
    dispatch(setProperty({ key: "pagination", value: __pagination }));
  }, [dashboardSelector.data?.pagination, dispatch]);

  let selectedStatus = dashboardSelector?.data?.["selectedStatus"];
  let statusCount = dashboardSelector?.data?.["statusCount"];
  let itemsPerPage = dashboardSelector?.data?.pagination?.["itemsPerPage"];

  const caseType = dashboardSelector?.data?.caseType;
  // If caseType is 'favourites', we set the selectedStatus to 'favourites' to handle pagination correctly.
  if (caseType === "favourites") {
    selectedStatus = "favourites";
    // Set the statusCount to an empty object if caseType is 'favourites'
    statusCount = { 'favourites': dashboardSelector?.data?.favourites?.total_count || 0 };
    // Set itemsPerPage to 10 if not defined
    itemsPerPage = itemsPerPage ? itemsPerPage : 10;
  }
  
  if(caseType === "all_cases") {
    // If caseType is 'all_cases', we set the selectedStatus to an empty string to handle pagination correctly.
    selectedStatus = "all_cases";
    // Set the statusCount to an empty object if caseType is 'all_cases'
    statusCount = { 'all_cases': dashboardSelector.data?.allCases?.total_count || 0 };
    // Set itemsPerPage to 10 if not defined
    itemsPerPage = itemsPerPage ? itemsPerPage : 10;
  }


  // prepareTotalPages function calculates the total number of pages based on the selected status and items per page.
  // It uses the status count to determine the number of records for the selected status.
  const prepareTotalPages = useCallback(() => {
    const statusId = selectedStatus;
    let recordCount = statusCount?.[statusId] ? statusCount?.[statusId] : 0;
    const pageCount = itemsPerPage ? Math.ceil(recordCount / itemsPerPage) : 0;
    setTotalPages(_ => pageCount);
    return { totalCount: recordCount, pageCount };
  }, [selectedStatus, statusCount, itemsPerPage]);

  /**
   * Calculates and updates the total number of pages and the current page's record count
   * based on the selected status, status count, and items per page.
   * 
   * Dependencies:
   * - Updates the total pages using `setTotalPages`.
   * - Updates the current page's record count using `setCurrentPageCount`.
   * 
   * @remarks
   * This function uses `useCallback` to memoize the logic and prevent unnecessary re-renders.
   * 
   * @dependencies
   * - `selectedStatus`: The currently selected status identifier.
   * - `statusCount`: An object mapping status IDs to their respective counts.
   * - `itemsPerPage`: The number of items displayed per page.
   * - `dashboardSelector?.data?.pagination`: The current pagination object from the dashboard selector.
   */
  const prepareTotalPageNumber = useCallback(() => {
    if (selectedStatus && statusCount && itemsPerPage) {
      const { totalCount } = prepareTotalPages();

      // Get current pagination object from dashboardSelector
      const __pagination = _.cloneDeep(dashboardSelector?.data?.pagination);
      // Update the total pages
      // Prepare shown records out of total records
      let shownRecords = __pagination["itemsPerPage"] * __pagination["currentPage"];
      shownRecords = shownRecords > totalCount ? totalCount : shownRecords;
      setCurrentPageCount(_ => `${shownRecords} / ${totalCount}`);
    }
  }, [selectedStatus, statusCount, itemsPerPage, dashboardSelector?.data?.pagination]);

  useEffect(() => {
    if (!dashboardSelector.data?.pagination?.["itemsPerPage"]) {
      handleItemsPerPgeLimit(PAGINATION_ITEMS_PER_PAGE[0]);
    }
  }, [handleItemsPerPgeLimit, dashboardSelector.data?.pagination]);

  useEffect(() => {
    prepareTotalPageNumber();
  }, [prepareTotalPageNumber]);

  useEffect(() => {
    // Reset the current page to the start page when the selected status changes
    setCurrentPage(START_PAGE);
  }, [selectedStatus, itemsPerPage,dashboardSelector.data?.offerSearch]);

  // Update the current page when the component mounts or when the selected status changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (dashboardSelector.data?.pagination?.["currentPage"] !== currentPage) {
        debouncedPaginationHandler(currentPage); // update the store
      }
    }, 500); // debounce delay

    return () => clearTimeout(timer); // cleanup on input change
  }, [currentPage]);


  /**
   * Handles the change of the current page in the pagination component.
   * Validates the new page number, updates the pagination state, and dispatches the changes.
   *
   * @param {Object} param - The parameter object.
   * @param {string | number} param.newPage - The new page number to navigate to.
   */
  const handleCurrentPageChange = ({ newPage }) => {
    // Validate the new page number. Set to START_PAGE if invalid.
    const numericNewPage = isNaN(parseInt(newPage)) ? START_PAGE : parseInt(newPage);
    const validatedPage = validatePage(numericNewPage);
    // Dispatch the updated pagination object
    // Get current pagination object from dashboardSelector
    const __pagination = _.cloneDeep(dashboardSelector.data?.pagination);
    // Update the limit per page
    __pagination["currentPage"] = validatedPage;
    // Dispatch the updated pagination object
    dispatch(setProperty({ key: "pagination", value: __pagination }));
    setCurrentPage(validatedPage);
  };

  /**
   * Handles keydown events for pagination navigation.
   * Allows users to navigate between pages using arrow keys.
   *
   * @param {KeyboardEvent} event - The keydown event object.
   */

  const handleKeyDown = (event) => {
    if (
      event.key === KEY_CODES.ARROW_DOWN ||
      event.key === KEY_CODES.ARROW_LEFT
    ) {
      handleCurrentPageChange({
        newPage: dashboardSelector.data?.pagination?.["currentPage"] - 1,
      });
    }
    if (
      event.key === KEY_CODES.ARROW_UP ||
      event.key === KEY_CODES.ARROW_RIGHT
    ) {
      handleCurrentPageChange({
        newPage: dashboardSelector.data?.pagination?.["currentPage"] + 1,
      });
    }
  };

  // Debounce the pagination handler to limit the number of calls
  // to the function when the user types in the input field
  const debouncedPaginationHandler = useCallback(
    _.debounce((newPage) => {
      handleCurrentPageChange({ newPage });
    }, 500),
    [handleCurrentPageChange]
  );

  // Hide the pagination if data loading
  if (dashboardSelector.loading || totalPages < 1) {
    return <></>;
  }

  return (
    <div className="dsh-case-paginattion">
      <StatusBar
        className="status-bar"
        style={{ justifyContent: "space-between" }}
      >
        <StatusBarSection>
          <StatusBarTextTitle>Items per page:</StatusBarTextTitle>
          <StatusBarText>
            <select
              className="item-per-page"
              value={
                dashboardSelector.data?.pagination?.["itemsPerPage"]
                  ? dashboardSelector.data?.pagination?.["itemsPerPage"]
                  : PAGINATION_ITEMS_PER_PAGE[0]
              }
              onChange={(event) => {
                handleItemsPerPgeLimit(event.target.value);
              }}
            >
              {PAGINATION_ITEMS_PER_PAGE.map((item) => (
                <option value={item.key} key={item.key}>
                  {item.value}
                </option>
              ))}
            </select>
          </StatusBarText>
        </StatusBarSection>

        <StatusBarPagination>
          <StatusBarPaginationIcon
            iconButtonProps={{
              "aria-label": "First Page",
              disabled:
                dashboardSelector.data?.pagination?.["currentPage"] ===
                START_PAGE,
              onClick: () => {
                handleCurrentPageChange({ newPage: START_PAGE });
              },
            }}
          >
            <FirstPageIcon />
          </StatusBarPaginationIcon>

          <StatusBarPaginationIcon
            iconButtonProps={{
              "aria-label": "Previous Page",
              disabled:
                dashboardSelector.data?.pagination?.["currentPage"] ===
                START_PAGE,
              onClick: () => {
                handleCurrentPageChange({
                  newPage:
                    dashboardSelector.data?.pagination?.["currentPage"] - 1,
                });
              },
            }}
          >
            <ChevronLeftIcon />
          </StatusBarPaginationIcon>
          <StatusBarPageGroup>
            <StatusBarTextTitle>Page</StatusBarTextTitle>
            <StatusBarPaginationInput
              onChange={(event) => {
                const newPage = (event.target as HTMLInputElement)?.value;
                setCurrentPage(newPage);
              }}
              onKeyDown={handleKeyDown}
              value={currentPage}
              aria-label={`text box, ${dashboardSelector.data?.pagination?.["currentPage"]} pages`}
              aria-live="assertive"
            />
            <StatusBarText>{`/ ${totalPages}`}</StatusBarText>
          </StatusBarPageGroup>
          <StatusBarPaginationIcon
            iconButtonProps={{
              "aria-label": "Next Page",
              disabled: dashboardSelector.data?.pagination?.["currentPage"] === totalPages,
              onClick: () => {
                handleCurrentPageChange({
                  newPage:
                    dashboardSelector.data?.pagination?.["currentPage"] + 1,
                });
              },
            }}
          >
            <ChevronRightIcon />
          </StatusBarPaginationIcon>

          <StatusBarPaginationIcon
            iconButtonProps={{
              "aria-label": "Last Page",
              disabled: dashboardSelector.data?.pagination?.["currentPage"] === totalPages,
              onClick: () => {
                handleCurrentPageChange({ newPage: totalPages });
              },
            }}
          >
            <LastPageIcon />
          </StatusBarPaginationIcon>
        </StatusBarPagination>
        <StatusBarSection>
          <StatusBarTextTitle>{"Count:"}</StatusBarTextTitle>
          <StatusBarText>
            <Tooltip
              style={{ display: "flex" }}
              closeOnReferenceHidden={false}
              placement="top"
              trigger="hover"
              tooltip="Number of records."
            >
              {currentPageCount}
            </Tooltip>
          </StatusBarText>
        </StatusBarSection>
      </StatusBar>
    </div>
  );
};

export default CasePagination;
